import * as React from "react"
import { BiTrash } from "react-icons/bi"
import { FaUser } from "react-icons/fa"
import { gql } from "@apollo/client"
import {
  AspectRatio,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Link,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import relative from "dayjs/plugin/relativeTime"
import Head from "next/head"
import NextLink from "next/link"
import { useRouter } from "next/router"

import {
  GetPostDocument,
  useCreateCommentMutation,
  useCreateUserPostMutation,
  useDestroyCommentMutation,
  useGetPostQuery,
} from "lib/graphql"
import { getVideoId, messageContainsEmailOrPhone, transformImage } from "lib/helpers/utils"
import { useForm } from "lib/hooks/useForm"
import { useMe } from "lib/hooks/useMe"
import { useMutationHandler } from "lib/hooks/useMutationHandler"
import yup from "lib/yup"
import { Checkbox } from "components/Checkbox"
import { Confirmation } from "components/Confirmation"
import { Form } from "components/Form"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { LoginForm } from "components/LoginForm"
import { Modal } from "components/Modal"
import { Textarea } from "components/Textarea"

dayjs.extend(relative)

const _ = gql`
  fragment CommentItemDetail on Comment {
    id
    message
    createdAt
    deletedAt
    isPrivate
    userId
    user {
      id
      fullName
      avatarUrl
    }
  }

  fragment PostDetail on Post {
    id
    title
    description
    type
    region
    categories
    deadline
    videoUrl
    createdAt
    user {
      id
      avatarUrl
      fullName
      interests
    }
    comments {
      items {
        ...CommentItemDetail
      }
      count
    }
    matchedUsers {
      items {
        id
        fullName
        avatarUrl
        customSlug
      }
      count
    }
    commenters {
      items {
        id
        fullName
        avatarUrl
      }
      count
    }
  }

  query GetPost($id: String!) {
    post(id: $id) {
      ...PostDetail
    }
  }

  mutation CreateComment($data: CreateCommentInput!) {
    createComment(data: $data) {
      id
    }
  }

  mutation CreateUserPost($postId: String!, $userId: String!) {
    createUserPost(postId: $postId, userId: $userId)
  }

  mutation DestroyComment($id: String!) {
    destroyComment(id: $id) {
      id
    }
  }
`

const CreateCommentSchema = yup.object().shape({
  message: yup.string().required("Verplicht"),
})

export default function PostDetail() {
  const { me } = useMe()
  const colorBlackWhite = useColorModeValue("black", "white")
  const colorGray = useColorModeValue("gray.600", "gray.200")
  const modalProps = useDisclosure()
  const matchModalProps = useDisclosure()

  const router = useRouter()
  const id = router.query.id as string
  const { data, loading, refetch } = useGetPostQuery({ variables: { id }, skip: !!!id })
  const handler = useMutationHandler()
  const [createComment, { loading: createCommentLoading }] = useCreateCommentMutation()
  const [createUserPost, { loading: createUserPostLoading }] = useCreateUserPostMutation()
  const [message, setMessage] = React.useState<string>("")

  const [destroyComment, { loading: destroyCommentLoading }] = useDestroyCommentMutation()

  const post = data?.post

  const defaultValues = { message: "", isPrivate: false }
  const form = useForm({ defaultValues, schema: CreateCommentSchema })

  const onSubmit = (values: typeof defaultValues) => {
    return form.handler(() => createComment({ variables: { data: { ...values, postId: id } } }), {
      onSuccess: (_, toast) => {
        form.reset()
        toast({ title: "Opmerking gemaakt" })
        refetch()
        setMessage("")
      },
    })
  }

  const handleDelete = (commentId: string) =>
    handler(() => destroyComment({ variables: { id: commentId } }), {
      onSuccess: (_, toast) => {
        refetch()
        toast({ description: "Opmerking verwijderd " })
      },
    })

  const handleAddMatch = (userId: string) => {
    if (!post) return
    handler(
      () => {
        return createUserPost({
          variables: { postId: post.id, userId },
          refetchQueries: [{ query: GetPostDocument, variables: { id: post.id } }],
        })
      },
      {
        onSuccess: (_, toast) => {
          matchModalProps.onClose()
          toast({
            title: "Match gemaakt",
          })
        },
      },
    )
  }

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Spinner />
      </Center>
    )
  if (!post) return <Text>Geen berichten gevonden</Text>

  const today = new Date().setHours(0, 0, 0, 0)
  const pastDeadline = new Date(post?.deadline).getTime() < new Date(today).getTime()
  const matched = post.matchedUsers.count > 0

  const showContainsEmailOrPhoneMessage = messageContainsEmailOrPhone(message) && !form.getValues().isPrivate

  return (
    <>
      <Head>
        <title>Kunstbende - {post.title}</title>
      </Head>
      <Limiter mt={16}>
        <Grid
          templateColumns={{ base: "repeat(1,1fr)", md: "repeat(5, 1fr)" }}
          gap={{ base: 0, md: 32 }}
          pb={120}
        >
          <GridItem colSpan={3}>
            <Stack spacing={8}>
              <HStack align="flex-start">
                <Badge
                  bg={post.type === "LOOKING" ? "pink.500" : "orange.500"}
                  color="black"
                  fontSize="2xl"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {post.type === "LOOKING" ? "Ik zoek" : "Ik bied"}
                </Badge>

                {matched && (
                  <Badge bg="blue.500" color="white" fontSize="2xl" whiteSpace="nowrap" overflow="hidden">
                    MIJN MATCHES
                  </Badge>
                )}
              </HStack>
              <Heading
                fontSize={{ base: 32, md: 42 }}
                fontWeight={900}
                lineHeight={{ base: "shorter", md: "short" }}
                px={0}
                textAlign="left"
                color={colorBlackWhite}
              >
                {post.title}
              </Heading>
              <Text
                fontSize="2xl"
                lineHeight={{ base: "shorter", md: "short" }}
                color="gray.400"
                maxW="650px"
                mt={2}
              >
                {post.region} / {post.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2)}
              </Text>
              <HStack align="center" mt={2}>
                <Text
                  fontSize="xl"
                  lineHeight={{ base: "shorter", md: "short" }}
                  color="gray.400"
                  maxW="650px"
                >
                  Deadline: {post.deadline && dayjs(post.deadline).format("DD/MM/YYYY")}
                </Text>
                {pastDeadline && (
                  <Badge bg="pink.500" color="white" fontSize={16} whiteSpace="nowrap" overflow="hidden">
                    Past Deadline
                  </Badge>
                )}
              </HStack>
              {post.videoUrl && (
                <AspectRatio _before={{ height: 0, content: "''", display: "block", paddingBottom: "55%" }}>
                  <iframe
                    title={post.title}
                    src={`https://www.youtube.com/embed/${getVideoId(post.videoUrl)}`}
                    allowFullScreen
                    className="iframe-height-restricted"
                    aria-label={`${post.title} video`}
                  />
                </AspectRatio>
              )}
              <Text color={colorGray} fontSize="xl">
                {post.description}
              </Text>
            </Stack>
          </GridItem>
          <GridItem colSpan={2} mt={{ base: 6, md: 2 }}>
            <LinkBox
              position="relative"
              overflow="hidden"
              p="2px"
              tabIndex={-1} 
              _focusWithin={{
                boxShadow: "0 0 0 3px var(--chakra-colors-blue-300)",
                outline: "none",
                borderRadius: "xl",
              }}
            >
              <LinkOverlay
                href={`/users/${post.user.id}`}
                textDecoration="none"
                aria-label={`View user ${post.user.fullName}`}
                tabIndex={0}
              >
                <HStack p={4}>
                  <Avatar
                    src={post.user.avatarUrl || undefined}
                    name={post.user.fullName || "avatar"}
                    w="70px"
                    h="70px"
                    bg="gray.600"
                    icon={<FaUser color="gray.900" size={18} />}
                    borderColor="white"
                    borderWidth={2}
                  />
                  <VStack align="left" pl={2} spacing={0}>
                    <Text fontSize="lg">{post.user.fullName}</Text>
                    <Text color="gray.500" fontSize="md">
                      {post?.user?.interests &&
                        post.user.interests.map(
                          (interest, index) =>
                            `${interest}${post.user.interests.length - 1 !== index ? " / " : ""}`,
                        )}
                    </Text>
                  </VStack>
                </HStack>
              </LinkOverlay>
            </LinkBox>
            {me ? (
              <Form onSubmit={onSubmit} {...form}>
                <Textarea
                  name="message"
                  label=""
                  placeholder="Comment..."
                  onChange={(e) => setMessage(e.target.value)}
                  aria-label="Reactie tekstvak"
                />
                {showContainsEmailOrPhoneMessage && (
                  <Box bg="pink.500" borderRadius="md" p={2} mt={2}>
                    <Text fontWeight="bold" lineHeight={1.3} aria-label="Bericht bevat e-mail of telefoonnummer">
                      Het lijkt erop alsof je een reactie gaat plaatsen met een e-mailadres of telefoonnummer.
                      Zorg dan dat je je reactie op privé zet.
                    </Text>
                  </Box>
                )}
                <Flex align="center" justify="space-between" w="100%" mt={2}>
                  <HStack>
                    <Checkbox
                      helpText="Vink aan om te zorgen dat jouw comments alleen te zien zijn door degene die de oproep heeft geplaatst. Gebruik deze instelling wanneer jij bijvoorbeeld jouw contactgegevens wil delen. "
                      label="Prive"
                      name="isPrivate"
                      color="gray.400"
                      aria-label="Privé checkbox"
                    />
                  </HStack>
                  <Button
                    bgColor="yellow.500"
                    color="black"
                    isLoading={createCommentLoading}
                    isDisabled={createCommentLoading || showContainsEmailOrPhoneMessage}
                    type="submit"
                    variant="solid"
                    aria-label="Verstuur reactie knop"
                  >
                    Verstuur
                  </Button>
                </Flex>
              </Form>
            ) : (
              <Button w="100%" colorScheme="yellow" variant="outline" onClick={modalProps.onOpen}>
                Log in om te reageren
              </Button>
            )}
            <Stack
              spacing={6}
              mt={8}
              maxH="350px"
              overflow="auto"
              css={{ "&::-webkit-scrollbar": { display: "none" } }}
            >
              {post.comments.items.map((item) => (
                <HStack key={item.id}>
                  <LinkBox position="relative" overflow="hidden">
                    <LinkOverlay href={`/users/${item.user.id}`} textDecoration="none">
                      <Avatar
                        src={item.user.avatarUrl || undefined}
                        name={item.user.fullName || "avatar"}
                        w="45px"
                        h="45px"
                        bg="gray.600"
                        icon={<FaUser color="gray.900" size={18} />}
                        aria-label={`${item.user.fullName || "Gebruiker"} avatar`}
                      />
                    </LinkOverlay>
                  </LinkBox>
                  <Stack spacing={0} flex={1}>
                    <HStack>
                      <Text display="flex" flexDir="row" alignItems="center" color="gray.500" fontSize="sm">
                        {item.user.fullName} - {dayjs(item.createdAt).fromNow()}{" "}
                      </Text>
                      {item.isPrivate && (
                        <Badge ml={2} bgColor="pink.500" color="white" px={2}>
                          Privé
                        </Badge>
                      )}
                      {item.user.id === me?.id && (
                        <Confirmation
                          onSubmit={() => handleDelete(item.id)}
                          title="Verwijderen Comment"
                          text="Weet je het zeker? Dit kan niet ongedaan worden gemaakt."
                          submitText="Indienen"
                          submitColor="pink"
                        >
                          <IconButton
                            size="xs"
                            aria-label="commentaar verwijderen"
                            ml={2}
                            color="red"
                            variant="ghost"
                            icon={<Box as={BiTrash} />}
                            isLoading={destroyCommentLoading}
                            isDisabled={destroyCommentLoading}
                          />
                        </Confirmation>
                      )}
                    </HStack>
                    <Text>{item.message}</Text>
                  </Stack>
                </HStack>
              ))}
            </Stack>
            <Stack mt={12} align="flex-start">
              {post.matchedUsers.count > 0 && (
                <>
                  <Text fontSize="xl" fontWeight="bold" mb={2}>
                    Matches:
                  </Text>
                  <SimpleGrid columns={{ base: 3, md: 5 }} spacing={1}>
                    {post.matchedUsers.items.map((user) => {
                      return (
                        <Tooltip
                          key={user.id}
                          label={user.fullName}
                          bg="yellow.500"
                          color="black"
                          fontWeight="bold"
                        >
                          <Box mb={2} mr={2}>
                            <NextLink href={`/users/${user.customSlug ? user.customSlug : user.id}`} passHref>
                              <Link _hover={{ textDecor: "none" }}>
                                <Avatar
                                  src={user.avatarUrl || undefined}
                                  name={user.fullName || "avatar"}
                                  w="65px"
                                  h="65px"
                                  bg="gray.600"
                                  icon={<FaUser color="gray.900" size={18} />}
                                  aria-label={`${user.fullName || "Gebruiker"} avatar`}
                                />
                              </Link>
                            </NextLink>
                          </Box>
                        </Tooltip>
                      )
                    })}
                  </SimpleGrid>
                </>
              )}
              {me?.id === post.user.id && (
                <Button
                  variant="outline"
                  colorScheme="yellow"
                  onClick={matchModalProps.onOpen}
                  isLoading={createUserPostLoading}
                >
                  Voeg een match toe
                </Button>
              )}
            </Stack>
          </GridItem>
        </Grid>
        <Modal {...matchModalProps} title="Voeg een match toe">
          {post.commenters.count === 0 && (
            <Text>Alleen leden die hebben gereageerd kunnen een match zijn</Text>
          )}
          {post.commenters.items.map((user) => (
            <Box
              key={user.id}
              w="100%"
              my={2}
              _hover={{ bgColor: "gray.800" }}
              onClick={() => handleAddMatch(user.id)}
            >
              <HStack p={4}>
                <Avatar
                  src={user.avatarUrl ? transformImage(user?.avatarUrl, "w_45,h_45,g_faces") : undefined}
                  name={user.fullName || "avatar"}
                  bg="gray.600"
                  icon={<FaUser color="gray.900" size={18} />}
                  boxSize="40px"
                  fontSize="sm"
                  borderRadius="50%"
                  aria-label={`${user.fullName || "Gebruiker"} avatar`}
                />
                <Text>{user.fullName}</Text>
              </HStack>
            </Box>
          ))}
        </Modal>
        <Modal {...modalProps}>
          <LoginForm handleSubmit={modalProps.onClose} />
        </Modal>
      </Limiter>
    </>
  )
}

PostDetail.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
